import { useMemo } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiClientService, queryKeys } from '@splitfiesta/core';
import type {
  ClaimLinkViewDto,
  LinkPickDto,
  LinkPickResultDto,
} from '@splitfiesta/shared';

import { isLineItemsConflict } from '@/hooks/queries/useClaimLineItems';
import { type UseApiReturn, useApi } from '@/hooks/useApi';

const POLL_MS = 10_000;

export const errorStatus = (error: unknown): number | undefined =>
  error && typeof error === 'object'
    ? (error as { status?: number }).status
    : undefined;

const unwrap = <T>(response: unknown): T =>
  ((response as { data?: T }).data ?? response) as T;

/** What the next GET answers after this pick. */
const viewAfterPick = (result: LinkPickResultDto): ClaimLinkViewDto =>
  result.status === 'open'
    ? { ...result, status: 'open' }
    : {
        status: 'stopped',
        expense: {
          title: result.expense.title,
          payer: { name: result.expense.payer.name },
        },
      };

// The token is the only credential: a signed-in visitor's bearer must not
// ride along, and a 401 here must not log them out.
function usePublicApi() {
  const api = useApi();
  return useMemo(() => {
    const quiet = { skipAuth: true, onUnauthorized: () => undefined };
    return {
      ...api,
      get: ((endpoint, options) =>
        api.get(endpoint, { ...options, ...quiet })) as UseApiReturn['get'],
      put: ((endpoint, body, options) =>
        api.put(endpoint, body, {
          ...options,
          ...quiet,
        })) as UseApiReturn['put'],
    };
  }, [api]);
}

export function useClaimLinkViewQuery(
  token: string,
  { poll }: { poll: boolean }
) {
  const api = usePublicApi();
  return useQuery({
    queryKey: queryKeys.claimLink(token),
    queryFn: async () =>
      unwrap<ClaimLinkViewDto>(
        await ApiClientService.claimLinks.view(api, token)
      ),
    enabled: !!token,
    // The app-wide 5-minute staleTime would reopen a reload on the persisted
    // view; a link stopped meanwhile must not show its old name list.
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: poll ? POLL_MS : false,
    // useApi already retries a dropped connection; 404 and 410 are answers.
    retry: false,
  });
}

/**
 * The body is the person's full set of picks, so a resend is idempotent.
 * Picks commute, so a conflict refetches the bill and resends once.
 */
export function useLinkPickMutation(token: string) {
  const api = usePublicApi();
  const queryClient = useQueryClient();
  return useMutation({
    networkMode: 'always',
    retry: false,
    scope: { id: `claim-link-${token}` },
    mutationFn: async (body: LinkPickDto) => {
      const send = async () =>
        unwrap<LinkPickResultDto>(
          await ApiClientService.claimLinks.pick(api, token, body)
        );
      try {
        return await send();
      } catch (error) {
        if (!isLineItemsConflict(error)) throw error;
        await queryClient
          .refetchQueries({ queryKey: queryKeys.claimLink(token) })
          .catch(() => undefined);
        return send();
      }
    },
    onSuccess: result => {
      queryClient.setQueryData(
        queryKeys.claimLink(token),
        viewAfterPick(result)
      );
    },
    onError: error => {
      if (isLineItemsConflict(error) || errorStatus(error) === 410) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.claimLink(token),
        });
      }
    },
  });
}
